import React from 'react';
import { token } from '@atlaskit/tokens';
import { NODE_TYPE } from './directionKeys.tsx';
import { STATE_COLOURS } from './ellipsis.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import type { OnTransitionReroutingClosePayload } from './ReroutingTransitionValid.types.tsx';
import { type Point, type PortIndex, toPortKey } from './toPortInfo.tsx';
import { useDraggableDataAttributes } from './useCanvasDragDropEvents.tsx';

const CORONA_GLOW_OPACITY = 0.45;

type Props = {
	position: Point;
	isImmutable: boolean;
	isGlowing?: boolean;
	parentStatusId?: string;
	portKey?: string;
	portIndex?: PortIndex;
	isRerouting?: boolean;
	isProposingTransition?: boolean;
	onHover?: (item: ItemReference | undefined) => void;
	onTransitionProposalClose?: (endPort: string | undefined) => void;
	onTransitionReroutingClose?: (value: OnTransitionReroutingClosePayload) => void;
};

const DiagramPort = ({
	position,
	portKey,
	isImmutable,
	isGlowing = false,
	parentStatusId,
	portIndex,
	isRerouting = false,
	isProposingTransition = false,
	onHover,
	onTransitionProposalClose,
	onTransitionReroutingClose,
}: Props): React.JSX.Element => {
	const dataAttributes = useDraggableDataAttributes('port', portKey);

	const onHoverChange = (isHovered: boolean) => {
		if (isProposingTransition && parentStatusId && portIndex !== undefined) {
			onTransitionProposalClose?.(isHovered ? toPortKey(parentStatusId, portIndex) : undefined);
			return;
		}

		if (isRerouting && parentStatusId && portIndex !== undefined) {
			onTransitionReroutingClose?.({
				proposedStatusId: parentStatusId,
				proposedPortKey: isHovered ? toPortKey(parentStatusId, portIndex) : undefined,
			});

			return;
		}

		if (isHovered && parentStatusId) {
			onHover?.({ id: parentStatusId, type: NODE_TYPE });

			return;
		}

		onHover?.(undefined);
	};

	const onPointerEnter = () => {
		onHoverChange(true);
	};

	const onPointerLeave = () => onHoverChange(false);

	return (
		<g
			onPointerEnter={onPointerEnter}
			onPointerLeave={onPointerLeave}
			pointerEvents={isImmutable ? 'none' : undefined}
			cursor={isImmutable ? undefined : 'crosshair'}
			data-testid="accessible-workflow-diagram.port"
			{...dataAttributes}
		>
			{/* Port glow */}
			<ellipse
				cx={position.x}
				cy={position.y}
				rx="10"
				ry="10"
				fill={STATE_COLOURS.focused}
				fillOpacity={isGlowing ? CORONA_GLOW_OPACITY : 0}
			/>
			{/* Port circle */}
			<ellipse
				x={position.x}
				y={position.y}
				cx={position.x}
				cy={position.y}
				rx="4"
				ry="4"
				fill={token('color.background.input')}
				stroke={STATE_COLOURS.focused}
				strokeWidth="2"
			/>
		</g>
	);
};

export default DiagramPort;
